import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useDataWithRoute, useTitleFromComponent, useUrlState } from "../../../../utils/hooks";
import { VsiBubble, VsiH4 } from "../../../../styled/App.styled";
import VsiLabeledSelect from "../../../../components/VsiLabeledSelect";
import { Card, Col, Row, Button } from "reactstrap";
import Stockchart from "../../../../components/Stockchart";
import Grid from "../../../../components/Grid";
import {
  formatDualAxisStockChartConfig,
  snakeCaseToTitleCase
} from "../../../../utils/Helpers";
import { useVsiAppContext } from "../../../../stores/VsiApp";
import {
  iopsLineColor,
  latencyLineColor, latencyLineColorSwap,
  mbsecLineColor, usedLineColor
} from "../../../../utils/chartUtils";
import { realTimeTrendDefs } from "../../../../utils/gridColumnDefs/storage/device/deviceTrends";

const metricTypes = [
  { value: "none", label: "None" },
  { value: "iops", label: "IOPS" },
  { value: "mbsec", label: "MB/Sec" },
  { value: "latency", label: "Latency" },
  { value: "blksize", label: "Avg Block Size" }
];

const chartTypes = [
  { value: "all_history", label: "All Interval", field: "" }, // Default
  { value: "by_volume", label: "By Volume", field: "volume" },
  { value: "by_host", label: "By Host", field: "host_name" },
  { value: "by_pool", label: "By Pool", field: "pool" }
];

export default function DeviceRealtimeTrends(props) {
  const { theme, client, user, periodId } = useVsiAppContext();
  const [formattedChartData, setFormattedChartData] = useState([]);
  const [chartConfig, setChartConfig] = useState({});
  const [isMunging, setIsMunging] = useState(true);
  const [params, setParams] = useUrlState({
    device_name: "",
    pool: "",
    volume: "",
    share: "",
    axis_1: "",
    axis_2: "",
    metric: "",
    navi_share: "",
    host_name: "",
    data_time: "interval"
  });

  const { device_name, metric, pool, volume, share, host_name, axis_1, axis_2, data_time } = params;

  useTitleFromComponent(`${device_name} / ${props.title}`);

  const getRouteForSelection = useCallback(() => {
    let routeToSearch = `/api/v6/report/device_realtime_trend_proc?client_id=${client.clientId}&period_id=${periodId}&device_name=${encodeURI(device_name)}`;
    if (metric.toLowerCase() === "all_history") {
      routeToSearch += `&chart_type=DEVICE&unit_name=${encodeURI(device_name)}`;
    } else if (metric.toLowerCase() === "by_pool") {
      routeToSearch += `&chart_type=POOL`;
      if (pool) {
        routeToSearch += `&unit_name=${encodeURIComponent(pool)}`;
      }
    } else if (metric.toLowerCase() === "by_volume") {
      routeToSearch += `&chart_type=LUN`;
      if (volume) {
        routeToSearch += `&unit_name=${encodeURIComponent(volume)}`;
      }
    } else if (metric.toLowerCase() === "by_host") {
      routeToSearch += `&chart_type=HOST`;
      if (host_name) {
        routeToSearch += `&unit_name=${encodeURIComponent(host_name)}`;
      }
    }
    return routeToSearch;
  }, [metric, pool, volume, share, device_name, host_name]);

  const [data, { isLoading }] = useDataWithRoute(getRouteForSelection(), data_time === "interval");

  const [records, options] = data?.recordsets ?? [[], []];

  const activeChartType = useMemo(() => {
    return chartTypes.find(option => metric === option.value) || chartTypes[0];
  }, [metric]);

  const additionalChartOptions = useMemo(() => {
    if (isLoading || metric === "all_history") return [];
    return options?.map?.(({ unit_name }) => ({
      value: unit_name,
      label: unit_name
    })) ?? [];
  }, [metric, options, isLoading]);

  const activeAdditionalChartOption = useMemo(() => {
    return additionalChartOptions?.find?.(option => option.value === params[activeChartType.field]) || additionalChartOptions?.[0] || {};
  }, [additionalChartOptions, activeChartType, params]);

  const axis1Options = useMemo(() => {
    return metricTypes?.filter?.(({ value }) => value !== axis_2) ?? [];
  }, [activeChartType, axis_2]);

  const axis2Options = useMemo(() => {
    return metricTypes?.filter?.(({ value }) => value !== axis_1) ?? [];
  }, [activeChartType, axis_1]);

  const activeAxis1Option = useMemo(() => {
    return axis1Options?.find?.(option => option.value === axis_1) || axis1Options[1];
  }, [axis1Options, axis_1]);

  const activeAxis2Option = useMemo(() => {
    return axis2Options?.find?.(option => option.value === axis_2) || axis2Options[0];
  }, [axis2Options, axis_2]);

  const defineChartConfigs = useCallback(() => {
    let returnFields = [];
    const axes = [activeAxis1Option, activeAxis2Option];
    for (let i = 0; i <= 1; i++) {
      switch (axes[i].value) {
        case "read_iops":
        case "write_iops":
        case "total_iops":
        case "iops":
          returnFields.push([
            {
              name: "IOPs (total)",
              field: "total_iops",
              yAxis: i,
              color: iopsLineColor,
              dataLabels: { format: "{point.y:,.0f}" },
              tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
            },
            {
              name: "IOPs (read)",
              field: "read_iops",
              yAxis: i,
              color: iopsLineColor,
              dashStyle: "Dash",
              dataLabels: { format: "{point.y:,.0f}" },
              tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
            },
            {
              name: "IOPs (write)",
              field: "write_iops",
              dashStyle: "Dot",
              yAxis: i,
              color: iopsLineColor,
              dataLabels: { format: "{point.y:,.0f}" },
              tooltip: { pointFormat: "{series.name}: <b>{point.y:,.0f}</b>" }
            }
          ]);
          break;
        case "latency":
        case "avg_latency":
        case "read_latency":
        case "write_latency":
          returnFields.push([
            {
              name: "Latency (avg)",
              field: "total_latency",
              yAxis: i,
              color: latencyLineColor
            },
            {
              name: "Latency (read)",
              field: "read_latency",
              yAxis: i,
              dashStyle: "Dash",
              color: latencyLineColor
            },
            {
              name: "Latency (write)",
              field: "write_latency",
              yAxis: i,
              dashStyle: "Dot",
              color: latencyLineColor
            }
          ]);
          break;
        case "mbsec":
          returnFields.push([
            {
              name: "MB/Sec (total)",
              field: "total_mbsec",
              yAxis: i,
              color: mbsecLineColor
            },
            {
              name: "MB/Sec (read)",
              field: "read_mbsec",
              yAxis: i,
              dashStyle: "Dash",
              color: mbsecLineColor
            },
            {
              name: "MB/Sec (write)",
              field: "write_mbsec",
              yAxis: i,
              dashStyle: "Dot",
              color: mbsecLineColor
            }
          ]);
          break;
        case "blksize":
          returnFields.push([
            {
              name: "Block Size (total)",
              field: "total_blksize",
              yAxis: i,
              color: usedLineColor
            },
            {
              name: "Block Size (read)",
              field: "read_blksize",
              yAxis: i,
              dashStyle: "Dash",
              color: usedLineColor
            },
            {
              name: "Block Size (write)",
              field: "write_blksize",
              yAxis: i,
              dashStyle: "Dot",
              color: usedLineColor
            }
          ]);
          break;
      }
    }
    // Detect repeat colors then replace them
    const colorAlreadyShown = new Set();
    let swapCount = { read_latency: 0, write_latency: 0, avg_latency: 0 };
    returnFields.map((innerRF) => {
      innerRF.map((rF) => {
        if (swapCount.hasOwnProperty(rF.field)) {
          if (colorAlreadyShown.has(rF.color)) {
            rF.color = latencyLineColorSwap[rF.field][swapCount[rF.field]];
          }
          colorAlreadyShown.add(rF.color);
          swapCount[rF.field] += 1;
        }
      });
    });
    return [].concat(...returnFields);
  }, [metric, activeAxis1Option, activeAxis2Option]);

  const handleStateWithData = useCallback((records) => {
    setIsMunging(true);
    const historyChartConfig = defineChartConfigs();
    const allHistoryConfig = formatDualAxisStockChartConfig(records || [], historyChartConfig);
    setChartConfig({
      yAxis: [
        {
          title: {
            text: activeAxis1Option.label
          }
        },
        {
          title: {
            text: activeAxis2Option.label
          },
          opposite: true
        }
      ],
      colors: historyChartConfig.map(({ color }) => color)
    });
    setFormattedChartData(allHistoryConfig.series);
    setIsMunging(false);
  }, [metric, activeAxis1Option, activeAxis2Option]);

  const onDataBtnClick = () => {
    setParams({
      data_time: "historical"
    });
  };

  const onAddtlChartOptionChange = useCallback(({ value }) => {
    setParams({
      [activeChartType.field]: value
    });
  }, [activeChartType]);

  const onUnitChange = ({ value }) => {
    setParams({
      metric: value
    });
  };

  const onAxis1Change = ({ value }) => {
    setParams({
      axis_1: value
    });
  };

  const onAxis2Change = ({ value }) => {
    setParams({
      axis_2: value
    });
  };

  useEffect(() => {
    handleStateWithData(records?.map?.(r => ({
      ...r,
      collection_date: Array.isArray(r.collection_date) ? r.collection_date[r.collection_date.length - 1] : r.collection_date
    })) ?? []);
  }, [records, metric, activeAxis1Option, activeAxis2Option]);

  const filterChanged = (eventData) => {
    let filteredData = [];
    eventData.api.forEachNodeAfterFilter(node => {
      filteredData.push(node.data);
    });

    handleStateWithData([filteredData]);
  };

  const showRtBtn = user.role.includes("super_user");

  return (
    <Card className={`report-card p-2 nav-card-${theme}`}>
      <VsiH4>Device Trends - {device_name}</VsiH4>
      <VsiBubble noAutobot>
        <div className={"w-100 p-2 d-flex flex-row justify-content-between"}>
          <div className={"d-flex"}>
            <VsiLabeledSelect options={chartTypes}
                              label={"Metric"}
                              onChange={onUnitChange}
                              minWidth={200}
                              defaultValue={activeChartType} />
            {activeChartType?.field ? (
              <VsiLabeledSelect options={additionalChartOptions}
                                isLoading={isLoading || isMunging}
                                isDisabled={isLoading || isMunging}
                                label={snakeCaseToTitleCase(activeChartType?.field ?? "")}
                                minWidth={200}
                                maxWidth={activeAdditionalChartOption?.value?.length > 15 ? 350 : 200}
                                onChange={onAddtlChartOptionChange}
                                defaultValue={activeAdditionalChartOption} />
            ) : null}
            {showRtBtn ? (
              <div className={"d-flex flex-row align-items-end"}>
                <Button onClick={onDataBtnClick}>
                  Historical View
                  <i className={"fa-solid fa-calendar-days ms-1"} />
                </Button>
              </div>
            ) : null}
          </div>
          <div className={"d-flex"}>
            <VsiLabeledSelect options={axis1Options}
                              isLoading={isLoading || isMunging}
                              isDisabled={isLoading || isMunging}
                              label={"Left Axis"}
                              minWidth={200}
                              maxWidth={225}
                              onChange={onAxis1Change}
                              defaultValue={activeAxis1Option} />
            <VsiLabeledSelect options={axis2Options}
                              isLoading={isLoading || isMunging}
                              isDisabled={isLoading || isMunging}
                              label={"Right Axis"}
                              minWidth={200}
                              maxWidth={225}
                              onChange={onAxis2Change}
                              defaultValue={activeAxis2Option} />
          </div>
        </div>
      </VsiBubble>
      <Row>
        <Col xs={12}>
          <VsiBubble
            unit={activeChartType?.value}
            autobotInfo={{
              secondaryMetric: [activeAdditionalChartOption?.value],
              yAxes: activeAxis2Option?.value ? [activeAxis1Option?.value, activeAxis2Option?.value] : [activeAxis1Option?.value]
            }}>
            <Stockchart series={formattedChartData}
                        saveParams={{
                          deviceName: device_name
                        }}
                        isLoading={isLoading}
                        config={chartConfig}
            />
          </VsiBubble>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <VsiBubble
            unit={activeChartType.value}
            autobotInfo={{
              secondaryMetric: [activeChartType?.value],
              yAxes: axis_2 ? [axis_1, axis_2] : [axis_1]
            }}>
            <Grid addStyle
                  data={records}
                  saveParams={{ metric: activeChartType.value }}
                  columnDefs={realTimeTrendDefs}
                  filterChanged={filterChanged}
            />
          </VsiBubble>
        </Col>
      </Row>
    </Card>
  );
}